// AbMapFieldCard — eine aufklappbare Karte je Feld (EBELN/EBELP/LABNR/MATNR/MFRPN/MENGE/NETPR/
// EEIND/WAERS): Erfassungsort (Scope) + methodenspezifische Eingaben + erkannter-Wert-Chip aus
// der letzten Vorschau. Vorbild: OC-Pilot MappingView.tsx (field-card/headerControls/lineControls/
// blockLineControls, NUR gelesen).

import React from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Chip, inputCls } from '../components/ui';
import AbMapPickButton from './AbMapPickButton';
import { describeField, applyScope, formatLabel, LINE_FORMAT_VALUES, BLOCK_FORMAT_VALUES, ALL_FORMAT_VALUES } from './rulesetModel';

const LOWER = { EBELN: 'ebeln', EBELP: 'ebelp', LABNR: 'labnr', MATNR: 'matnr', MFRPN: 'mfrpn', MENGE: 'menge', NETPR: 'netpr', EEIND: 'eeind', WAERS: 'waers' };
// Kopf-Formate (Exclude<Format, 'isoweek_week_column'|'price_per_unit'|'price_per_pe_code'> im TS-Original).
const HEADER_FORMAT_VALUES = ALL_FORMAT_VALUES.filter((f) => !['isoweek_week_column', 'price_per_unit', 'price_per_pe_code'].includes(f));

function RecognizedChip({ isDarkMode, t, preview, field }) {
  if (!preview || !Array.isArray(preview.lines)) return null;
  const vals = preview.lines.map((l) => l[LOWER[field]]).filter((v) => v != null && v !== '');
  if (!vals.length) return <Chip tone="slate" isDarkMode={isDarkMode}>{t('ab.map.recognizedEmpty')}</Chip>;
  const label = vals.length > 1 ? t('ab.map.recognizedValueMore', { value: vals[0], n: vals.length - 1 }) : t('ab.map.recognizedValue', { value: vals[0] });
  return <Chip tone="green" isDarkMode={isDarkMode}>{label}</Chip>;
}

const NumberField = ({ isDarkMode, label, value, onChange, min, max, width = 'w-16' }) => (
  <label className={`text-xs flex items-center gap-1.5 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
    {label}
    <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className={`${inputCls(isDarkMode)} ${width} py-1`} />
  </label>
);

const AbMapFieldCard = ({ isDarkMode, t, field, fm, form, canDelivery, expanded, onToggleExpand, onFieldChange, pick, onPick, preview }) => {
  const textMuted = isDarkMode ? 'text-white/50' : 'text-slate-500';
  const picking = pick && pick.field === field;

  const setScope = (scope) => {
    onFieldChange(applyScope(fm, scope, form.strategy));
    onToggleExpand(scope === 'off' ? null : field);
  };

  const headerControls = () => (
    <div className="space-y-2 pt-2">
      <label className="text-xs flex items-center gap-2">
        <span className={textMuted}>{t('ab.map.headerMethodLabel')}</span>
        <select value={fm.headerMethod} onChange={(e) => onFieldChange({ headerMethod: e.target.value })} className={`${inputCls(isDarkMode)} py-1`}>
          <option value="box">{t('ab.map.headerMethod.box')}</option>
          <option value="regex">{t('ab.map.headerMethod.regex')}</option>
          <option value="presence">{t('ab.map.headerMethod.presence')}</option>
        </select>
      </label>

      {fm.headerMethod === 'box' && (
        <div className="flex items-center gap-2 flex-wrap">
          <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'header-box'} label={t('ab.map.headerBoxPick')} onClick={() => onPick({ field, kind: 'header-box' })} />
          {fm.box && <code className={`text-xs ${textMuted}`}>{t('ab.map.headerBoxValue', { page: fm.box.page, x0: fm.box.x0, x1: fm.box.x1 })}</code>}
        </div>
      )}

      {fm.headerMethod === 'regex' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'header-regex'} label={t('ab.map.rowEndPick')} onClick={() => onPick({ field, kind: 'header-regex' })} />
            <input value={fm.regexPattern || ''} placeholder={t('ab.map.headerRegexPlaceholder')} onChange={(e) => onFieldChange({ regexPattern: e.target.value })} className={`${inputCls(isDarkMode)} flex-1 min-w-[160px] font-mono text-xs py-1`} />
            <NumberField isDarkMode={isDarkMode} label={t('ab.map.headerGroupLabel')} value={fm.group ?? 1} onChange={(v) => onFieldChange({ group: v })} min={0} max={9} />
          </div>
          <label className="text-xs flex items-center gap-2">
            <span className={textMuted}>{t('ab.map.formatLabel')}</span>
            <select value={fm.headerFormat || 'text'} onChange={(e) => onFieldChange({ headerFormat: e.target.value })} className={`${inputCls(isDarkMode)} py-1`}>
              {HEADER_FORMAT_VALUES.map((f) => <option key={f} value={f}>{formatLabel(t, f)}</option>)}
            </select>
          </label>
          <p className={`text-xs ${textMuted}`}>{t('ab.map.patternPickHint')}</p>
        </div>
      )}

      {fm.headerMethod === 'presence' && (
        <div className="flex items-center gap-2 flex-wrap">
          <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'header-presence'} label={t('ab.map.rowEndPick')} onClick={() => onPick({ field, kind: 'header-presence' })} />
          <input value={fm.presenceValue || ''} placeholder={t('ab.map.headerPresencePlaceholder')} onChange={(e) => onFieldChange({ presenceValue: e.target.value })} className={`${inputCls(isDarkMode)} flex-1 min-w-[120px] py-1`} />
        </div>
      )}
    </div>
  );

  const lineColumnControls = () => {
    const seq = fm.lineMode === 'sequence';
    return (
      <div className="space-y-2 pt-2">
        <label className="text-xs flex items-center gap-2">
          <span className={textMuted}>{t('ab.map.lineSourceLabel')}</span>
          <select
            value={seq ? 'sequence' : 'extract'}
            onChange={(e) => onFieldChange(e.target.value === 'sequence' ? { lineMode: 'sequence' } : { lineMode: 'column' })}
            className={`${inputCls(isDarkMode)} py-1`}
          >
            <option value="extract">{t('ab.map.lineSource.extract')}</option>
            <option value="sequence">{t('ab.map.lineSource.sequence')}</option>
          </select>
        </label>

        {seq ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-xs ${textMuted}`}>{t('ab.map.sequenceHint')}</span>
            <NumberField isDarkMode={isDarkMode} label={t('ab.map.sequenceStartLabel')} value={fm.rowStart ?? 10} onChange={(v) => onFieldChange({ rowStart: v })} min={0} />
            <NumberField isDarkMode={isDarkMode} label={t('ab.map.sequenceStepLabel')} value={fm.rowStep ?? 10} onChange={(v) => onFieldChange({ rowStep: v })} min={1} />
          </div>
        ) : (
          <>
            <label className="text-xs flex items-center gap-2">
              <span className={textMuted}>{t('ab.map.formatLabel')}</span>
              <select value={fm.format} onChange={(e) => onFieldChange({ format: e.target.value })} className={`${inputCls(isDarkMode)} py-1`}>
                {LINE_FORMAT_VALUES.map((f) => <option key={f} value={f}>{formatLabel(t, f)}</option>)}
              </select>
            </label>
            {fm.format === 'price_per_unit' ? (
              <div className="flex items-center gap-2 flex-wrap">
                <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'line-column'} label={t('ab.map.columnPick')} onClick={() => onPick({ field, kind: 'line-column' })} />
                {fm.xMin != null && <code className={`text-xs ${textMuted}`}>{t('ab.map.columnValue', { min: fm.xMin, max: fm.xMax })}</code>}
                <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'line-unit'} label={t('ab.map.unitPick')} onClick={() => onPick({ field, kind: 'line-unit' })} />
                {fm.unitXMin != null && <code className={`text-xs ${textMuted}`}>{t('ab.map.columnValue', { min: fm.unitXMin, max: fm.unitXMax })}</code>}
                <NumberField isDarkMode={isDarkMode} label={t('ab.map.decimalsLabel')} value={fm.decimals ?? 6} onChange={(v) => onFieldChange({ decimals: v })} min={0} max={8} />
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'line-column'} label={t('ab.map.columnPick')} onClick={() => onPick({ field, kind: 'line-column' })} />
                {fm.xMin != null ? <code className={`text-xs ${textMuted}`}>{t('ab.map.columnValue', { min: fm.xMin, max: fm.xMax })}</code> : <span className={`text-xs ${textMuted}`}>{t('ab.map.columnNone')}</span>}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const blockControls = () => {
    const ppu = fm.format === 'price_per_unit' || fm.format === 'price_per_pe_code';
    return (
      <div className="space-y-2 pt-2">
        <label className="text-xs flex items-center gap-2">
          <span className={textMuted}>{t('ab.map.formatLabel')}</span>
          <select value={fm.format} onChange={(e) => onFieldChange({ format: e.target.value })} className={`${inputCls(isDarkMode)} py-1`}>
            {BLOCK_FORMAT_VALUES.map((f) => <option key={f} value={f}>{formatLabel(t, f)}</option>)}
          </select>
        </label>
        {ppu ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs ${textMuted}`}>{t('ab.map.blockPpuPatternLabel')}</span>
            <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'block-ppu-pattern'} label={t('ab.map.rowEndPick')} onClick={() => onPick({ field, kind: 'block-ppu-pattern' })} />
            <input value={fm.ppuPattern || ''} onChange={(e) => onFieldChange({ ppuPattern: e.target.value })} className={`${inputCls(isDarkMode)} flex-1 min-w-[160px] font-mono text-xs py-1`} />
            <NumberField isDarkMode={isDarkMode} label={t('ab.map.blockPpuPriceGroupLabel')} value={fm.ppuPriceGroup ?? 1} onChange={(v) => onFieldChange({ ppuPriceGroup: v })} min={1} max={9} />
            <NumberField isDarkMode={isDarkMode} label={fm.format === 'price_per_pe_code' ? t('ab.map.blockPpuCodeGroupLabel') : t('ab.map.blockPpuUnitGroupLabel')} value={fm.ppuUnitGroup ?? 2} onChange={(v) => onFieldChange({ ppuUnitGroup: v })} min={1} max={9} />
            <NumberField isDarkMode={isDarkMode} label={t('ab.map.decimalsLabel')} value={fm.decimals ?? 6} onChange={(v) => onFieldChange({ decimals: v })} min={0} max={8} />
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs ${textMuted}`}>{t('ab.map.blockPatternLabel')}</span>
            <AbMapPickButton isDarkMode={isDarkMode} active={picking && pick.kind === 'line-pattern'} label={t('ab.map.rowEndPick')} onClick={() => onPick({ field, kind: 'line-pattern' })} />
            <input value={fm.linePattern || ''} placeholder={t('ab.map.blockPatternPlaceholder')} onChange={(e) => onFieldChange({ linePattern: e.target.value })} className={`${inputCls(isDarkMode)} flex-1 min-w-[160px] font-mono text-xs py-1`} />
            <NumberField isDarkMode={isDarkMode} label={t('ab.map.blockGroupLabel')} value={fm.lineGroup ?? 1} onChange={(v) => onFieldChange({ lineGroup: v })} min={0} max={9} />
          </div>
        )}
        <p className={`text-xs ${textMuted}`}>{t('ab.map.patternPickHint')}</p>
      </div>
    );
  };

  return (
    <div className={`rounded-xl px-3 py-2.5 transition-colors ${picking ? (isDarkMode ? 'ring-1 ring-amber-400/50 bg-amber-500/5' : 'ring-1 ring-amber-300 bg-amber-50/60') : (isDarkMode ? 'bg-white/[0.03]' : 'bg-slate-50')}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => onToggleExpand(expanded ? null : field)} className={`inline-flex items-center gap-1.5 text-sm font-medium ${isDarkMode ? 'text-white/85' : 'text-slate-800'}`}>
          {fm.scope === 'off' ? <span className="w-3.5 inline-block" /> : (expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
          <span className="font-mono">{field}</span>
          <span className={`font-normal ${textMuted}`}>{t(`ab.field.${field.toLowerCase()}`)}</span>
        </button>
        <div className="flex-1" />
        <select value={fm.scope} onChange={(e) => setScope(e.target.value)} className={`${inputCls(isDarkMode)} py-1 text-xs`}>
          <option value="off">{t('ab.map.scope.off')}</option>
          <option value="header">{t('ab.map.scope.header')}</option>
          <option value="line">{form.strategy === 'blocks' ? t('ab.map.scope.lineBlock') : t('ab.map.scope.lineColumn')}</option>
          {canDelivery && <option value="delivery">{t('ab.map.scope.delivery')}</option>}
        </select>
      </div>

      {fm.scope !== 'off' && (
        <div className="flex items-center gap-2 flex-wrap mt-1 pl-5">
          <span className={`text-xs ${textMuted}`}>{describeField(fm, t)}</span>
          <RecognizedChip isDarkMode={isDarkMode} t={t} preview={preview} field={field} />
        </div>
      )}

      {expanded && fm.scope === 'header' && <div className="pl-5">{headerControls()}</div>}
      {expanded && fm.scope === 'line' && <div className="pl-5">{form.strategy === 'blocks' ? blockControls() : lineColumnControls()}</div>}
    </div>
  );
};

export default AbMapFieldCard;
